import { useEffect, useState, useCallback, useRef } from 'react';
import { MobileHeader, HeaderAction, MobileToolbar, Chip, ActionSheet } from '../components/MobileHeader';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval,
  addWeeks, subWeeks, isSameDay, isToday, addDays, subDays,
  startOfMonth, endOfMonth, isSameMonth, addMonths, subMonths
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus, MapPin, Clock, Users, Calendar, LayoutGrid, Hotel, Ticket, Edit2, Trash2, LogOut, LogIn, ArrowLeft, CalendarDays, ChevronDown, UserRound, User, X, SlidersHorizontal } from 'lucide-react';
import { useLang } from '../i18n';
import { api } from '../api';
import ClassModal from '../components/ClassModal';
import { FestivalModal, ConflictModal, ConfirmedToggle, TRANSPORT_TYPES } from './Festivals';
import YearView from '../components/YearView';
import { planningRange } from '../lib/prefetchPaths';
import { eventDays } from '../lib/eventDays';
import { calendarLook, CALENDAR_COLORS } from '../lib/calendarLook';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const fmtDuration = (mins) => `${Math.floor(mins / 60)}h${mins % 60 ? (mins % 60) : ''}`;
const WEEK_DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const WEEK_DAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ── Festival display helpers ──────────────────────────────────────────────
function festDisplayStart(f) {
  try {
    const d = parseISO(f.date_start + 'T00:00:00');
    return f.teach_on_departure_day ? addDays(d, 1) : d;
  } catch { return new Date(f.date_start); }
}
function festDisplayEnd(f) {
  try {
    const d = parseISO(f.date_end + 'T00:00:00');
    return f.teach_on_return_day ? subDays(d, 1) : d;
  } catch { return new Date(f.date_end); }
}

const TRANSPORT_EMOJI = { plane: '✈', train: '🚄', car: '🚗' };

function FestIcons({ festival, size = 12 }) {
  const hasTransport = festival.transport_type && festival.transport_confirmed;
  const hasHotel = festival.hotel_confirmed;
  const hasTicket = festival.ticket_confirmed;
  if (!hasTransport && !hasHotel && !hasTicket) return null;
  return (
    <span className="inline-flex items-center gap-0.5 mr-0.5 shrink-0">
      {hasTransport && (
        <span className="text-white/90 leading-none" style={{ fontSize: size }}>
          {TRANSPORT_EMOJI[festival.transport_type] || '✈'}
        </span>
      )}
      {hasHotel && <Hotel size={size} className="text-amber-200" />}
      {hasTicket && <Ticket size={size} className="text-pink-200" />}
    </span>
  );
}

export default function Planning() {
  const { t, lang } = useLang();
  const locale = lang === 'fr' ? fr : undefined;
  const location = useLocation();
  const navigate = useNavigate();
  const fromStudent = location.state?.fromStudent;
  const [openEditId, setOpenEditId] = useState(location.state?.openEditFor || null);
  const focusClassId = location.state?.openEditFor || null;

  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  // v2.16 : plus de vue Semaine (comme sur le téléphone) — une ancienne préférence « week » repasse sur « Mois »
  const [viewMode, setViewMode] = useState(() => { const v = sessionStorage.getItem('ds_view_mode'); return v === 'year' ? 'year' : 'month'; });
  const setViewModePersist = (m) => { setViewMode(m); sessionStorage.setItem('ds_view_mode', m); };

  // Mobile : pas de bouton Semaine → la page s'ouvre toujours sur le Mois
  // (la vue Semaine reste accessible en touchant un jour du calendrier)
  useEffect(() => {
    if (viewMode === 'week' && window.matchMedia('(max-width: 1023px)').matches) setViewModePersist('month');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSeq = useRef(0);

  // Mobile : glisser le doigt sur le calendrier pour changer de mois.
  // Le calendrier suit le doigt ; on modifie directement son style (pas de re-rendu React à chaque mouvement).
  const swipe = useRef(null);       // { x, y, horizontal }
  const monthRef = useRef(null);
  const setMonthStyle = (transform, transition = 'none') => {
    if (!monthRef.current) return;
    monthRef.current.style.transition = transition;
    monthRef.current.style.transform = transform;
  };
  const onSwipeStart = (e) => {
    const t = e.touches[0];
    swipe.current = { x: t.clientX, y: t.clientY, horizontal: null };
  };
  const onSwipeMove = (e) => {
    const s = swipe.current;
    if (!s) return;
    const t = e.touches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (s.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) s.horizontal = Math.abs(dx) > Math.abs(dy);
    if (s.horizontal) setMonthStyle(`translateX(${dx}px)`);
  };
  const onSwipeEnd = (e) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s?.horizontal) return;  // simple toucher ou défilement vertical : on ne fait rien
    const dx = e.changedTouches[0].clientX - s.x;
    if (Math.abs(dx) < 60) { setMonthStyle('translateX(0)', 'transform 150ms ease-out'); return; }
    // Bascule totale ≈ 0,1 s : 50 ms de sortie + 50 ms d'entrée
    const next = dx < 0;
    setMonthStyle(`translateX(${next ? '-100%' : '100%'})`, 'transform 50ms ease-in');
    setTimeout(() => {
      if (next) nextPeriod(); else prevPeriod();
      setMonthStyle(`translateX(${next ? '30%' : '-30%'})`);
      requestAnimationFrame(() => requestAnimationFrame(() => setMonthStyle('translateX(0)', 'transform 50ms ease-out')));
    }, 50);
  };
  const [classes, setClasses] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [groupCourses, setGroupCourses] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addDefaultDate, setAddDefaultDate] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterCity, setFilterCity] = useState('');
  const [filterCourseId, setFilterCourseId] = useState('');
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [showFestivalModal, setShowFestivalModal] = useState(false);
  const [editFestival, setEditFestival] = useState(null);
  const [festsOpen, setFestsOpen] = useState(false);
  const [pastFestsOpen, setPastFestsOpen] = useState(false);
  const [showNewSheet, setShowNewSheet] = useState(false);  // mobile : menu « + Nouveau » en bas de l'écran
  const [newItemDate, setNewItemDate] = useState(null);     // date pré-remplie (yyyy-MM-dd) d'un nouvel événement / festival
  const [newClassType, setNewClassType] = useState('private');
  const [hiddenChips, setHiddenChips] = useState(new Set());
  const [showFilterChips, setShowFilterChips] = useState(false);
  const [gcalEvents, setGcalEvents] = useState([]);
  const [gcalConnected, setGcalConnected] = useState(false);
  // v2.19 : agendas Google, tous affichés d'office ; ceux qu'on cache sont gardés dans le profil (PC = téléphone)
  const [calendars, setCalendars] = useState([]);
  const [hiddenCals, setHiddenCals] = useState(new Set());
  const toggleCal = (id) => setHiddenCals(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id);
    api.post('/profile/settings', { hidden_calendars: [...n] }).catch(() => {});
    return n;
  });
  // v2.21 : clic droit (appui long sur téléphone) sur une pastille → couleur et style plein/transparent de l'agenda
  const [calStyles, setCalStyles] = useState({});
  const [calMenu, setCalMenu] = useState(null);  // agenda dont le menu est ouvert
  const pressTimer = useRef(null);
  const setCalStyle = (id, patch) => setCalStyles(prev => {
    const n = { ...prev, [id]: { ...(prev[id] || {}), ...patch } };
    api.post('/profile/settings', { calendar_styles: n }).catch(() => {});
    return n;
  });
  const shownGcal = gcalEvents.filter(e => !hiddenCals.has(e.calendar_id))
    .map(e => { const l = calendarLook(e, calStyles); return { ...e, calendar_color: l.color, solid: l.solid }; });
  const [personalEvents, setPersonalEvents] = useState([]);
  const [showPersonalModal, setShowPersonalModal] = useState(false);
  const [editPersonalEvent, setEditPersonalEvent] = useState(null);
  const [gtaskEvents, setGtaskEvents] = useState([]);

  // Derive display month: add 6 days to ensure we're in the actual displayed month
  const displayMonth = startOfMonth(addDays(currentWeekStart, 6));

  // Jump to focused date when coming from student profile
  useEffect(() => {
    if (location.state?.focusDate) {
      try {
        const date = parseISO(location.state.focusDate);
        setCurrentWeekStart(startOfWeek(date, { weekStartsOn: 1 }));
        setSelectedDay(date);
      } catch (e) { console.error('Invalid focusDate in location state:', e); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open editor when openEditFor is set (after classes are loaded)
  // Brief 400ms delay so user sees the week context before the modal opens
  useEffect(() => {
    if (!openEditId || !classes.length) return;
    const cls = classes.find(c => c.id === openEditId);
    if (cls) {
      const timer = setTimeout(() => {
        setSelectedClass(cls);
        setOpenEditId(null);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [classes, openEditId]);

  const weekDays = eachDayOfInterval({
    start: currentWeekStart,
    end: endOfWeek(currentWeekStart, { weekStartsOn: 1 })
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentWeekStart and viewMode are the real triggers; all others are stable
  const fetchClasses = useCallback(async () => {
    const { start, end } = planningRange(currentWeekStart);
    const reqId = ++fetchSeq.current;
    // Google (Agenda, Tasks) est plus lent : il arrive après, sans retenir l'affichage des cours.
    Promise.all([
      api.get(`/gtasks/events?start=${start}&end=${end}`).catch(() => []),
      api.get(`/gcal/events?start=${start}&end=${end}`).catch(() => []),
    ]).then(([gTaskData, gcalData]) => {
      if (reqId !== fetchSeq.current) return;
      setGtaskEvents(gTaskData || []);
      setGcalEvents(gcalData || []);
    });
    // v2.18 : cours, festivals, événements et état Google en une seule demande (préchargée pour le mois en cours)
    const d = await api.get(`/planning/data?start=${start}&end=${end}`).catch(() => null);  // v2.29 : réseau coupé → pas de plantage
    if (!d || reqId !== fetchSeq.current) return;  // un mois plus récent a été demandé entre-temps (glissements rapides)
    setClasses(d.classes);
    setFestivals(d.festivals || []);
    setPersonalEvents(d.personal_events || []);
    setGcalConnected(d.google?.connected || false);
  }, [currentWeekStart]);

  useEffect(() => {
    fetchClasses();
    api.get('/locations').then(setLocations);
    api.get('/students').then(setAllStudents);
    api.get('/group-courses').then(setGroupCourses);
    api.get('/gcal/calendars').then(c => setCalendars(c || [])).catch(() => {});
    api.get('/profile/settings').then(st => {
      setHiddenCals(new Set(st?.hidden_calendars || []));
      setCalStyles(st?.calendar_styles || {});
    }).catch(() => {});
  }, [fetchClasses]);

  const prevPeriod = () => {
    if (viewMode === 'month') {
      const prev = subMonths(displayMonth, 1);
      setCurrentWeekStart(startOfWeek(startOfMonth(prev), { weekStartsOn: 1 }));
    } else {
      setCurrentWeekStart(w => subWeeks(w, 1));
    }
  };

  const nextPeriod = () => {
    if (viewMode === 'month') {
      const next = addMonths(displayMonth, 1);
      setCurrentWeekStart(startOfWeek(startOfMonth(next), { weekStartsOn: 1 }));
    } else {
      setCurrentWeekStart(w => addWeeks(w, 1));
    }
  };

  const applyFilters = (list) => {
    let filtered = list.filter(c => c.status !== 'cancelled' && !c.is_cancelled);
    if (filterType !== 'all') filtered = filtered.filter(c => c.type === filterType);
    if (filterCity) filtered = filtered.filter(c => c.location?.city === filterCity);
    if (filterCourseId) filtered = filtered.filter(c => c.group_course_id === filterCourseId);
    if (hiddenChips.size > 0) {
      filtered = filtered.filter(c => {
        if (c.type === 'group') return !hiddenChips.has(c.group_course_id || '__group');
        if (c.type === 'private') return !hiddenChips.has('private');
        return true;
      });
    }
    return filtered;
  };

  const festivalsForDay = (day) => {
    if (hiddenChips.has('festivals')) return [];
    const dayStr = format(day, 'yyyy-MM-dd');
    return festivals.filter(f => {
      const ds = format(festDisplayStart(f), 'yyyy-MM-dd');
      const de = format(festDisplayEnd(f), 'yyyy-MM-dd');
      return ds <= dayStr && de >= dayStr;
    });
  };

  const classesForDay = (day) => {
    const list = classes.filter(c => {
      try { return isSameDay(parseISO(c.datetime_start), day); } catch { return false; }
    });
    return applyFilters(list).sort((a, b) => a.datetime_start.localeCompare(b.datetime_start));
  };

  const handleClassSaved = () => { fetchClasses(); setSelectedClass(null); setShowAddModal(false); };

  const toggleChip = (id) => setHiddenChips(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const filteredFestivals = hiddenChips.has('festivals') ? [] : festivals;

  const gcalEventsForDay = (day) => shownGcal.filter(e => eventDays(e).includes(format(day, 'yyyy-MM-dd')));  // v2.19 : chaque jour couvert

  const personalEventsForDay = (day) => personalEvents.filter(e => {
    try { return isSameDay(parseISO(e.datetime_start), day); } catch { return false; }
  });

  const gtaskEventsForDay = (day) => gtaskEvents.filter(e => {
    try { return isSameDay(parseISO(e.datetime_start), day); } catch { return false; }
  });

  const handleAddOnDay = (day) => {
    setAddDefaultDate(format(day, "yyyy-MM-dd'T'18:00"));
    setShowAddModal(true);
  };

  const handleMonthDayClick = (day) => {
    setSelectedDay(day);
    // Mobile : on reste sur le mois, la liste du jour s'affiche dessous
    if (window.matchMedia('(max-width: 1023px)').matches) {
      setTimeout(() => dayListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      return;
    }
    setCurrentWeekStart(startOfWeek(day, { weekStartsOn: 1 }));
    setViewModePersist('week');
  };

  const dayListRef = useRef(null);
  const daySelectedInMonth = isSameMonth(selectedDay, displayMonth);

  // Liste des événements d'un jour (mobile : vue Semaine et sous le calendrier du mois)
  const renderDayList = (day) => (
    <>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-slate-800 capitalize font-manrope">
          {format(day, 'EEEE d MMMM', { locale })}
        </h2>
        <button onClick={() => handleAddOnDay(day)} className="flex items-center gap-1 text-sm text-[#0288D1] font-semibold">
          <Plus size={15} /> Ajouter
        </button>
      </div>
      {/* Festival block for mobile */}
      {festivalsForDay(day).map(f => (
        <div key={f.id} className="mb-3 rounded-2xl px-4 py-3 text-white flex items-center gap-3"
          style={{ backgroundColor: f.color || '#F59E0B' }}
          data-testid="festival-mobile-block">
          <FestIcons festival={f} size={16} />
          <div>
            <div className="font-bold text-sm">{f.name}</div>
            <div className="text-xs opacity-80">{f.city}{f.country ? `, ${f.country}` : ''}</div>
          </div>
        </div>
      ))}
      {classesForDay(day).length === 0 && festivalsForDay(day).length === 0 && gcalEventsForDay(day).length === 0 && personalEventsForDay(day).length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">{t('planning.noClasses')}</div>
      ) : (
        <div className="space-y-3">
          {classesForDay(day).map(cls => (
            <MobileClassCard key={cls.id} cls={cls}
              onClick={cls.type === 'group' && cls.group_course_id
                ? () => navigate(`/group-classes/${cls.group_course_id}`)
                : () => setSelectedClass(cls)}
            />
          ))}
          {gcalEventsForDay(day).map(evt => (
            <MobileGCalCard key={evt.id} evt={evt} />
          ))}
          {personalEventsForDay(day).map(evt => (
            <MobilePersonalCard key={evt.id} evt={evt}
              onClick={() => { setEditPersonalEvent(evt); setShowPersonalModal(true); }} />
          ))}
          {gtaskEventsForDay(day).map(evt => (
            <MobileGTaskCard key={evt.id} evt={evt} />
          ))}
        </div>
      )}
    </>
  );

  const cities = [...new Set(locations.map(l => l.city).filter(Boolean))];

  const periodLabel = viewMode === 'month'
    ? format(displayMonth, 'MMMM yyyy', { locale })
    : `${format(currentWeekStart, 'd')} – ${format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'd MMM yyyy', { locale })}`;

  return (
    <div className="max-w-5xl mx-auto" data-testid="planning-page">
      {/* Back to student banner */}
      {fromStudent && (
        <button
          onClick={() => navigate(`/students/${fromStudent.id}`)}
          className="flex items-center gap-2 text-sm font-semibold text-[#0288D1] bg-[#0288D1]/8 px-3 py-2 rounded-xl mb-4 hover:bg-[#0288D1]/15 transition-colors border border-[#0288D1]/20"
          data-testid="back-to-student-btn"
        >
          <ArrowLeft size={15} />
          Retour à {fromStudent.name}
        </button>
      )}
      {/* Mobile : en-tête commun + barre de puces */}
      <MobileHeader title={t('planning.title')} actions={<>
        <HeaderAction icon={SlidersHorizontal} label="Filtres" active={showFilterChips} onClick={() => setShowFilterChips(s => !s)}
          badge={(filterType !== 'all') + hiddenChips.size || null} testId="filters-btn-mobile" />
        <HeaderAction icon={Plus} label="Nouveau" primary onClick={() => setShowNewSheet(true)} testId="add-class-btn-mobile" />
      </>} />
      {/* « + » mobile : crée à la date du jour touché dans le calendrier (aujourd'hui par défaut) */}
      <ActionSheet open={showNewSheet} onClose={() => setShowNewSheet(false)} testId="new-class-sheet"
        title={<span className="capitalize">Nouveau · {format(selectedDay, 'EEEE d MMMM', { locale })}</span>} actions={[
        { icon: UserRound, label: 'Cours privé', className: 'text-[#26A69A]', onClick: () => { setNewClassType('private'); handleAddOnDay(selectedDay); } },
        { icon: User, label: 'Événement personnel', onClick: () => { setEditPersonalEvent(null); setNewItemDate(format(selectedDay, 'yyyy-MM-dd')); setShowPersonalModal(true); } },
        { icon: Ticket, label: 'Festival', className: 'text-amber-600', onClick: () => { setEditFestival(null); setNewItemDate(format(selectedDay, 'yyyy-MM-dd')); setShowFestivalModal(true); } },
      ]} />
      <MobileToolbar className="!mb-2">
        {[['month', 'Mois'], ['year', 'Année']].map(([val, label]) => (
          <Chip key={val} active={viewMode === val} onClick={() => setViewModePersist(val)} color="#334155">{label}</Chip>
        ))}
        {/* ‹ Octobre 2026 › sur la même ligne (remplace la carte de navigation sur mobile) */}
        {viewMode !== 'year' && (
          <div className="flex-1 shrink-0 flex items-center justify-center">
            <button onClick={prevPeriod} aria-label="Précédent" data-testid="prev-period-btn-mobile"
              className="w-9 h-9 flex items-center justify-center rounded-full text-slate-600 active:bg-slate-100">
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm font-bold text-slate-800 font-manrope capitalize whitespace-nowrap" data-testid="period-label-mobile">{periodLabel}</span>
            <button onClick={nextPeriod} aria-label="Suivant" data-testid="next-period-btn-mobile"
              className="w-9 h-9 flex items-center justify-center rounded-full text-slate-600 active:bg-slate-100">
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </MobileToolbar>
      {calendars.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="calendar-chips">
          {calendars.map(c => {
            const hidden = hiddenCals.has(c.id);
            return (
              <button key={c.id} title={`${hidden ? 'Afficher' : 'Cacher'} · clic droit : couleur et style`}
                onClick={() => { if (pressTimer.current === 'opened') { pressTimer.current = null; return; } toggleCal(c.id); }}
                onContextMenu={e => { e.preventDefault(); setCalMenu(c); }}
                onPointerDown={e => { if (e.pointerType !== 'mouse') pressTimer.current = setTimeout(() => { pressTimer.current = 'opened'; setCalMenu(c); }, 500); }}
                onPointerUp={() => { if (pressTimer.current !== 'opened') clearTimeout(pressTimer.current); }}
                onPointerLeave={() => { if (pressTimer.current !== 'opened') clearTimeout(pressTimer.current); }}
                className={cn('flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border transition-all',
                  hidden ? 'bg-white text-slate-400 border-slate-200 line-through' : 'bg-white text-slate-700 border-slate-200')}
                data-testid={`calendar-chip-${c.name}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: hidden ? '#CBD5E1' : (calStyles[c.id]?.color || c.color) }} />
                {c.name}
              </button>
            );
          })}
        </div>
      )}
      <ActionSheet open={!!calMenu} onClose={() => setCalMenu(null)} title={calMenu ? `Agenda · ${calMenu.name}` : ''} testId="calendar-menu">
        {calMenu && (() => {
          const cur = calStyles[calMenu.id] || {};
          const curColor = cur.color || calMenu.color;
          return (
            <div className="px-4 pb-4 space-y-4">
              <div>
                <div className="text-xs font-bold text-slate-500 mb-2">Couleur</div>
                <div className="flex flex-wrap gap-2">
                  {[calMenu.color, ...CALENDAR_COLORS.filter(x => x.toLowerCase() !== (calMenu.color || '').toLowerCase())].map((col, k) => (
                    <button key={col} onClick={() => setCalStyle(calMenu.id, { color: k === 0 ? null : col })}
                      title={k === 0 ? 'Couleur de Google' : col} data-testid={`calendar-color-${k}`}
                      className={cn('w-8 h-8 rounded-full border-2', curColor === col ? 'border-slate-900' : 'border-white shadow')}
                      style={{ backgroundColor: col }} />
                  ))}
                </div>
                <div className="text-[11px] text-slate-400 mt-1">La 1re = couleur de Google Agenda</div>
              </div>
              <div>
                <div className="text-xs font-bold text-slate-500 mb-2">Style</div>
                <div className="flex gap-2">
                  {[[true, 'Plein'], [false, 'Transparent']].map(([solid, label]) => (
                    <button key={label} onClick={() => setCalStyle(calMenu.id, { solid })} data-testid={`calendar-style-${label}`}
                      className={cn('flex-1 h-11 rounded-xl text-sm font-bold border-2',
                        !!cur.solid === solid ? 'border-slate-900' : 'border-transparent')}
                      style={solid ? { backgroundColor: curColor, color: '#fff' }
                        : { backgroundColor: `${curColor}22`, color: '#1E293B', borderLeft: `4px solid ${curColor}` }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}
      </ActionSheet>

      {/* Header */}
      <div className="mobile-hidden flex flex-col sm:flex-row sm:items-center justify-end gap-3 mb-5">
        <div className="flex flex-wrap items-center gap-2">
          {/* View mode toggle */}
          <div className="flex bg-slate-100 rounded-xl p-1 gap-0.5">
            <button
              onClick={() => setViewModePersist('week')}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
                viewMode === 'week' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              )}
              data-testid="view-week-btn"
            >
              <Calendar size={13} /> Semaine
            </button>
            <button
              onClick={() => setViewModePersist('month')}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
                viewMode === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              )}
              data-testid="view-month-btn"
            >
              <LayoutGrid size={13} /> Mois
            </button>
            <button
              onClick={() => setViewModePersist('year')}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
                viewMode === 'year' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              )}
              data-testid="view-year-btn"
            >
              <CalendarDays size={13} /> Année
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className={cn('bg-white rounded-2xl border border-slate-100 shadow-sm p-1.5 lg:p-3 mb-3 lg:mb-4', viewMode !== 'week' && 'mobile-hidden')}>
        <div className={cn('flex items-center justify-between', viewMode === 'week' && 'mb-2 lg:mb-3')}>
          <button
            onClick={prevPeriod}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="prev-period-btn"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="font-bold text-slate-800 text-sm font-manrope capitalize">{periodLabel}</span>
          <button
            onClick={nextPeriod}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="next-period-btn"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Week day pills — only in week mode */}
        {viewMode === 'week' && (
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map(day => {
              const count = classes.filter(c => { try { return isSameDay(parseISO(c.datetime_start), day); } catch { return false; } }).length;
              const active = isSameDay(day, selectedDay);
              const today = isToday(day);
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDay(day)}
                  className={cn(
                    'flex flex-col items-center py-2 rounded-xl transition-colors text-xs',
                    active ? 'bg-[#0288D1] text-white' : 'hover:bg-slate-50',
                    today && !active ? 'ring-2 ring-[#0288D1]/30' : ''
                  )}
                  data-testid={`day-btn-${format(day, 'yyyy-MM-dd')}`}
                >
                  <span className={cn('font-medium capitalize', active ? 'text-white/80' : 'text-slate-500')}>
                    {format(day, 'EEE', { locale }).replace('.', '')}
                  </span>
                  <span className={cn('font-extrabold text-sm font-manrope', active ? 'text-white' : 'text-slate-900')}>
                    {format(day, 'd')}
                  </span>
                  {count > 0 && (
                    <span className={cn('w-1.5 h-1.5 rounded-full mt-0.5', active ? 'bg-white/80' : 'bg-[#0288D1]')} />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className={cn('space-y-2', showFilterChips ? 'mb-4' : 'mb-2')}>
        {/* Row 1: type chips + festivals toggle + advanced toggle (PC ; sur mobile → barre de puces) */}
        <div className="mobile-hidden flex flex-wrap items-center gap-2">
          {[['all', 'Tous'], ['group', t('cls.group')], ['private', t('cls.private')]].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilterType(val)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors',
                filterType === val ? 'bg-[#0288D1] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              )}
              data-testid={`filter-${val}`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => toggleChip('festivals')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all border',
              hiddenChips.has('festivals')
                ? 'bg-white text-slate-400 border-slate-200 opacity-50'
                : 'bg-amber-500 text-white border-amber-500'
            )}
            data-testid="filter-festivals-chip"
          >
            <CalendarDays size={13} /> Festivals
          </button>
          {gcalConnected && (
            <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#4285F4]/10 text-[#4285F4] text-xs font-bold border border-[#4285F4]/20" title="Google Calendar connecté">
              G Cal ✓
            </span>
          )}
          {(groupCourses.length > 0 || cities.length > 0) && (
            <button
              onClick={() => setShowFilterChips(s => !s)}
              className="ml-auto flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="toggle-filter-chips-btn"
            >
              <ChevronDown size={13} className={cn('transition-transform duration-200', showFilterChips && 'rotate-180')} />
              Filtres
            </button>
          )}
        </div>

        {/* Mobile : type de cours + festivals, en haut du panneau Filtres */}
        {showFilterChips && (
          <div className="mobile-only flex flex-wrap gap-2">
            {[['all', 'Tous'], ['group', t('cls.group')], ['private', t('cls.private')]].map(([val, label]) => (
              <Chip key={val} active={filterType === val} onClick={() => setFilterType(val)}>{label}</Chip>
            ))}
            <Chip active={!hiddenChips.has('festivals')} onClick={() => toggleChip('festivals')} color="#F59E0B" icon={CalendarDays}>Festivals</Chip>
          </div>
        )}

        {/* Row 2 (collapsible): per-course chips + private + city/course selects */}
        {showFilterChips && (
          <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100">
            {groupCourses.map(c => {
              const isHidden = hiddenChips.has(c.id);
              return (
                <button key={c.id} onClick={() => toggleChip(c.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all',
                    isHidden ? 'opacity-40 bg-white border-slate-200 text-slate-500' : 'text-white border-transparent'
                  )}
                  style={!isHidden ? { backgroundColor: c.color || '#0288D1' } : {}}
                  data-testid={`chip-course-${c.id}`}
                >
                  {c.name}
                </button>
              );
            })}
            <button
              onClick={() => toggleChip('private')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all',
                hiddenChips.has('private') ? 'opacity-40 bg-white border-slate-200 text-slate-500' : 'bg-[#26A69A] text-white border-transparent'
              )}
              data-testid="chip-private"
            >
              Privés
            </button>
            {cities.length > 0 && (
              <select
                value={filterCity}
                onChange={e => setFilterCity(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white border border-slate-200 text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                data-testid="filter-city-select"
              >
                <option value="">Toutes villes</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {groupCourses.length > 0 && (
              <select
                value={filterCourseId}
                onChange={e => setFilterCourseId(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white border border-slate-200 text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                data-testid="filter-course-select"
              >
                <option value="">Tous les cours</option>
                {groupCourses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>
        )}
      </div>

      {/* YEAR VIEW */}
      {viewMode === 'year' && (
        <YearView courses={groupCourses} />
      )}

      {/* MONTH VIEW */}
      {viewMode === 'month' && (
        <div onTouchStart={onSwipeStart} onTouchMove={onSwipeMove} onTouchEnd={onSwipeEnd} className="overflow-hidden" data-testid="month-swipe">
          <div ref={monthRef} style={{ touchAction: 'pan-y' }}>
            <MonthView
              classes={applyFilters(classes)}
              festivals={filteredFestivals}
              gcalEvents={shownGcal}
              personalEvents={personalEvents}
              gtaskEvents={gtaskEvents}
              displayMonth={displayMonth}
              locale={locale}
              lang={lang}
              onDayClick={handleMonthDayClick}
              selectedDay={daySelectedInMonth ? selectedDay : null}
            />
          </div>
        </div>
      )}

      {/* Mobile : événements du jour touché, sous le calendrier du mois */}
      {viewMode === 'month' && daySelectedInMonth && (
        <div ref={dayListRef} className="mobile-only block mt-4" style={{ scrollMarginTop: '4.5rem' }} data-testid="month-day-list">
          {renderDayList(selectedDay)}
        </div>
      )}

      {/* WEEK VIEW */}
      {viewMode === 'week' && (
        <>
          {/* Desktop: 7-column grid */}
          <div className="mobile-hidden">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-2 mb-1">
              {weekDays.map(day => {
                const today = isToday(day);
                return (
                  <div key={day.toISOString()} className="text-center py-1.5">
                    <div className={cn('text-xs font-bold capitalize', today ? 'text-[#0288D1]' : 'text-slate-500')}>
                      {format(day, 'EEE', { locale }).replace('.', '')}
                    </div>
                    <div className={cn('text-lg font-extrabold font-manrope', today ? 'text-[#0288D1]' : 'text-slate-800')}>
                      {format(day, 'd')}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Festival strip — spanning blocks */}
            {(() => {
              const weekStart = weekDays[0];
              const weekEnd = weekDays[6];
              const weekFestivals = filteredFestivals.filter(f => {
                const ds = format(festDisplayStart(f), 'yyyy-MM-dd');
                const de = format(festDisplayEnd(f), 'yyyy-MM-dd');
                const ws = format(weekStart, 'yyyy-MM-dd');
                const we = format(weekEnd, 'yyyy-MM-dd');
                return ds <= we && de >= ws;
              });
              if (weekFestivals.length === 0) return null;
              return (
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {weekFestivals.map(f => {
                    const dispStart = format(festDisplayStart(f), 'yyyy-MM-dd');
                    const dispEnd = format(festDisplayEnd(f), 'yyyy-MM-dd');
                    const startIdx = Math.max(0, weekDays.findIndex(d => format(d, 'yyyy-MM-dd') >= dispStart));
                    const endIdx = Math.min(6, (() => {
                      for (let i = 6; i >= 0; i--) {
                        if (format(weekDays[i], 'yyyy-MM-dd') <= dispEnd) return i;
                      }
                      return 0;
                    })());
                    const isClipped_left = dispStart < format(weekStart, 'yyyy-MM-dd');
                    const isClipped_right = dispEnd > format(weekEnd, 'yyyy-MM-dd');
                    return (
                      <div key={f.id}
                        style={{
                          gridColumn: `${startIdx + 1} / ${endIdx + 2}`,
                          backgroundColor: f.color || '#F59E0B',
                        }}
                        className={cn(
                          'px-3 py-1.5 text-white min-h-[36px] flex items-center gap-1.5 transition-opacity',
                          isClipped_left ? 'rounded-r-xl' : isClipped_right ? 'rounded-l-xl' : 'rounded-xl',
                          f.optional ? 'opacity-40' : 'opacity-100'
                        )}
                        data-testid="festival-week-block"
                      >
                        <FestIcons festival={f} size={12} />
                        <div className="min-w-0">
                          <div className="text-xs font-bold truncate leading-tight">{f.name}</div>
                          <div className="text-[10px] opacity-75 truncate">
                            {f.city}{f.country ? `, ${f.country}` : ''}
                            {f.teach_on_departure_day && ' · cours J-départ'}
                            {f.teach_on_return_day && ' · cours J-retour'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Day columns — classes only */}
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map(day => {
                const dayClasses = classesForDay(day);
                const hasFestival = festivalsForDay(day).some(f => !f.optional);
                return (
                  <div key={day.toISOString()} className={cn('min-h-[180px]', hasFestival && 'opacity-60')}>
                    <div className="space-y-1.5">
                      {dayClasses.map(cls => (
                        <SmallClassCard
                          key={cls.id}
                          cls={cls}
                          highlighted={cls.id === focusClassId}
                          onClick={cls.type === 'group' && cls.group_course_id
                            ? () => navigate(`/group-classes/${cls.group_course_id}`)
                            : () => setSelectedClass(cls)}
                        />
                      ))}
                      {gcalEventsForDay(day).map(evt => (
                        <GCalEventCard key={evt.id} evt={evt} />
                      ))}
                      {personalEventsForDay(day).map(evt => (
                        <PersonalEventCard key={evt.id} evt={evt}
                          onClick={() => { setEditPersonalEvent(evt); setShowPersonalModal(true); }} />
                      ))}
                      {gtaskEventsForDay(day).map(evt => (
                        <GTaskEventCard key={evt.id} evt={evt} />
                      ))}
                      <button
                        onClick={() => handleAddOnDay(day)}
                        className="w-full py-1.5 border border-dashed border-slate-200 rounded-lg text-slate-300 hover:border-[#0288D1]/50 hover:text-[#0288D1] transition-colors text-xs"
                        data-testid={`add-on-day-${format(day, 'yyyy-MM-dd')}`}
                      >+</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile: selected day */}
          <div className="mobile-only">
            {renderDayList(selectedDay)}
          </div>
        </>
      )}

      {selectedClass && (
        <ClassModal classData={selectedClass} locations={locations} allStudents={allStudents} onClose={() => setSelectedClass(null)} onSaved={handleClassSaved} />
      )}
      {showAddModal && (
        <ClassModal classData={null} defaultDatetime={addDefaultDate} prefilledData={{ type: newClassType }} locations={locations} allStudents={allStudents} onClose={() => setShowAddModal(false)} onSaved={handleClassSaved} />
      )}

      {/* ── Festival section ── */}
      <div className="mt-8">
        {(() => {
          const today = new Date(); today.setHours(0,0,0,0);
          const upcomingFests = festivals.filter(f => {
            try { return new Date(f.date_end) >= today; } catch { return true; }
          }).sort((a, b) => a.date_start?.localeCompare(b.date_start));
          const pastFests = festivals.filter(f => {
            try { return new Date(f.date_end) < today; } catch { return false; }
          }).sort((a, b) => b.date_start?.localeCompare(a.date_start));

          const FestTable = ({ fests, isPast }) => (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Festival</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Dates</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Destination</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Départ</th>
                      <th className="text-left px-3 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Retour</th>
                      <th className="text-center px-3 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Confirmé</th>
                      <th className="text-right px-3 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Coût</th>
                      <th className="px-3 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {fests.map((f, idx) => {
                      const fmtD = (d) => { try { return format(parseISO(d), 'd MMM yy', { locale }); } catch { return d || '—'; } };
                      const totalCost = (f.total_cost || 0);
                      return (
                        <tr key={f.id}
                          className={cn('border-b border-slate-50 hover:bg-slate-50/40 transition-colors group', idx === fests.length - 1 && 'border-b-0', isPast && 'opacity-70')}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: f.color || '#F59E0B' }} />
                              <span className={cn('font-semibold text-xs', f.optional ? 'text-slate-400' : 'text-slate-900')}>{f.name}</span>
                              {f.optional && (
                                <span className="text-[9px] font-bold bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-full border border-slate-200">?</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">
                            {fmtD(f.date_start)} → {fmtD(f.date_end)}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600">
                            {[f.city, f.country].filter(Boolean).join(', ') || '—'}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1 text-xs text-slate-600">
                              <LogOut size={11} className="text-slate-400 shrink-0" />
                              <span>{f.departure_city || '—'}</span>
                              <button
                                onClick={async () => { await api.put(`/festivals/${f.id}`, { teach_on_departure_day: !f.teach_on_departure_day }); fetchClasses(); }}
                                title={f.teach_on_departure_day ? "Cours le jour du départ (actif)" : "Pas de cours le jour du départ"}
                                className={cn('shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold border transition-colors',
                                  f.teach_on_departure_day
                                    ? 'bg-amber-100 text-amber-700 border-amber-200'
                                    : 'bg-slate-100 text-slate-400 border-slate-200 hover:border-amber-200 hover:text-amber-600'
                                )}>
                                cours
                              </button>
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1 text-xs text-slate-600">
                              <LogIn size={11} className="text-slate-400 shrink-0" />
                              <span>{f.return_arrival_city || '—'}</span>
                              <button
                                onClick={async () => { await api.put(`/festivals/${f.id}`, { teach_on_return_day: !f.teach_on_return_day }); fetchClasses(); }}
                                title={f.teach_on_return_day ? "Cours le jour du retour (actif)" : "Pas de cours le jour du retour"}
                                className={cn('shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold border transition-colors',
                                  f.teach_on_return_day
                                    ? 'bg-amber-100 text-amber-700 border-amber-200'
                                    : 'bg-slate-100 text-slate-400 border-slate-200 hover:border-amber-200 hover:text-amber-600'
                                )}>
                                cours
                              </button>
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center justify-center gap-2">
                              {(() => {
                                const transportEmoji = f.transport_type === 'train' ? '🚄' : f.transport_type === 'car' ? '🚗' : '✈';
                                const transportColor = f.transport_type === 'train' ? 'text-purple-500' : f.transport_type === 'car' ? 'text-green-500' : 'text-sky-500';
                                return (<>
                                  <ConfirmedToggle
                                    festivalId={f.id} field="transport_confirmed" value={f.transport_confirmed}
                                    iconOn={<span className={cn('text-sm', transportColor)}>{transportEmoji}</span>}
                                    iconOff={<span className="text-slate-300 text-sm">{transportEmoji}</span>}
                                    onSaved={fetchClasses}
                                  />
                                  <ConfirmedToggle
                                    festivalId={f.id} field="hotel_confirmed" value={f.hotel_confirmed}
                                    iconOn={<Hotel size={14} className="text-amber-500" />}
                                    iconOff={<Hotel size={14} className="text-slate-300" />}
                                    onSaved={fetchClasses}
                                  />
                                  <ConfirmedToggle
                                    festivalId={f.id} field="ticket_confirmed" value={f.ticket_confirmed}
                                    iconOn={<Ticket size={14} className="text-pink-500" />}
                                    iconOff={<Ticket size={14} className="text-slate-300" />}
                                    onSaved={fetchClasses}
                                  />
                                </>);
                              })()}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            {totalCost > 0 ? (
                              <span className="text-xs font-bold text-slate-700">{totalCost} CHF</span>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={async () => { await api.put(`/festivals/${f.id}`, { optional: !f.optional }); fetchClasses(); }}
                                title={f.optional ? 'Marquer comme confirmé' : 'Marquer comme optionnel'}
                                className={cn('px-1.5 py-0.5 rounded-full text-[9px] font-bold border transition-colors',
                                  f.optional ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-white text-slate-400 border-slate-200 hover:border-slate-400'
                                )}>
                                {f.optional ? 'optionnel' : '?'}
                              </button>
                              <button onClick={() => { setEditFestival(f); setShowFestivalModal(true); }}
                                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100"
                                data-testid={`edit-festival-planning-${f.id}`}>
                                <Edit2 size={12} className="text-slate-400" />
                              </button>
                              <button onClick={async () => {
                                if (!window.confirm(`Supprimer "${f.name}" ?`)) return;
                                await api.delete(`/festivals/${f.id}`);
                                toast.success('Festival supprimé');
                                fetchClasses();
                              }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50">
                                <Trash2 size={12} className="text-red-400" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );

          return (
            <>
              {/* Upcoming festivals — repliable */}
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={() => setFestsOpen(v => !v)}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  data-testid="toggle-fests-btn">
                  <Ticket size={16} className="text-amber-500" />
                  <h2 className="font-bold text-slate-800 text-sm font-manrope">
                    Festivals <span className="text-slate-400 font-normal">({upcomingFests.length})</span>
                  </h2>
                  <ChevronDown size={14} className={cn('text-slate-400 transition-transform', !festsOpen && '-rotate-90')} />
                </button>
              </div>
              {festsOpen && (upcomingFests.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm bg-white rounded-2xl border border-slate-100">
                  Aucun festival à venir — utilisez "+ Nouveau → Festival" pour en ajouter.
                </div>
              ) : (
                <FestTable fests={upcomingFests} isPast={false} />
              ))}

              {/* Past festivals — repliable, fermé par défaut */}
              {pastFests.length > 0 && (
                <div className="mt-6">
                  <button
                    onClick={() => setPastFestsOpen(v => !v)}
                    className="flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity w-full"
                    data-testid="toggle-past-fests-btn">
                    <Ticket size={16} className="text-slate-400" />
                    <h2 className="font-bold text-slate-500 text-sm font-manrope">
                      Festivals passés <span className="text-slate-400 font-normal">({pastFests.length})</span>
                    </h2>
                    <ChevronDown size={14} className={cn('text-slate-400 transition-transform', !pastFestsOpen && '-rotate-90')} />
                  </button>
                  {pastFestsOpen && <FestTable fests={pastFests} isPast={true} />}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {showFestivalModal && (
        <FestivalModal
          festival={editFestival}
          defaultDate={newItemDate}
          onClose={() => { setShowFestivalModal(false); setEditFestival(null); setNewItemDate(null); }}
          onSaved={() => { setShowFestivalModal(false); setEditFestival(null); setNewItemDate(null); fetchClasses(); }}
        />
      )}
      {showPersonalModal && (
        <PersonalEventModal
          event={editPersonalEvent}
          defaultDate={newItemDate}
          onClose={() => { setShowPersonalModal(false); setEditPersonalEvent(null); setNewItemDate(null); }}
          onSaved={() => { setShowPersonalModal(false); setEditPersonalEvent(null); setNewItemDate(null); fetchClasses(); }}
        />
      )}
    </div>
  );
}

function MonthView({ classes, festivals = [], gcalEvents = [], personalEvents = [], gtaskEvents = [], displayMonth, locale, lang, onDayClick, selectedDay = null }) {
  const monthStart = startOfMonth(displayMonth);
  const monthEnd = endOfMonth(displayMonth);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calDays = eachDayOfInterval({ start: calStart, end: calEnd });
  const DAYS = lang === 'fr' ? WEEK_DAYS_FR : WEEK_DAYS_EN;

  // Group into week rows
  const weekRows = [];
  for (let i = 0; i < calDays.length; i += 7) weekRows.push(calDays.slice(i, i + 7));

  const classesByDay = {};
  classes.forEach(cls => {
    try {
      const key = format(parseISO(cls.datetime_start), 'yyyy-MM-dd');
      if (!classesByDay[key]) classesByDay[key] = [];
      classesByDay[key].push(cls);
    } catch (e) { console.error('Invalid datetime_start for class:', cls?.id, e); }
  });

  // v2.25 : un événement Google sur plusieurs jours = une bande (comme les festivals), en haut de la semaine
  const gcalBands = gcalEvents.filter(evt => eventDays(evt).length > 1);
  const gcalByDay = {};
  gcalEvents.filter(evt => eventDays(evt).length <= 1).forEach(evt => eventDays(evt).forEach(key => {
    if (!gcalByDay[key]) gcalByDay[key] = [];
    gcalByDay[key].push(evt);
  }));

  const personalByDay = {};
  personalEvents.forEach(evt => {
    try {
      const key = format(parseISO(evt.datetime_start), 'yyyy-MM-dd');
      if (!personalByDay[key]) personalByDay[key] = [];
      personalByDay[key].push(evt);
    } catch {}
  });

  const gtaskByDay = {};
  gtaskEvents.forEach(evt => {
    try {
      const key = format(parseISO(evt.datetime_start), 'yyyy-MM-dd');
      if (!gtaskByDay[key]) gtaskByDay[key] = [];
      gtaskByDay[key].push(evt);
    } catch {}
  });

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="month-view">
      {/* Header */}
      <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/50">
        {DAYS.map(d => <div key={d} className="text-center text-xs font-bold text-slate-400 py-1.5">{d}</div>)}
      </div>

      {weekRows.map((weekRow, rowIdx) => {
        const weekStartStr = format(weekRow[0], 'yyyy-MM-dd');
        const weekEndStr = format(weekRow[6], 'yyyy-MM-dd');

        // Festivals visible in this week row
        const weekFests = (festivals || []).filter(f => {
          const ds = format(festDisplayStart(f), 'yyyy-MM-dd');
          const de = format(festDisplayEnd(f), 'yyyy-MM-dd');
          return ds <= weekEndStr && de >= weekStartStr;
        });

        const weekBands = gcalBands.filter(evt => { const d = eventDays(evt); return d[0] <= weekEndStr && d[d.length - 1] >= weekStartStr; });

        return (
          <div key={weekStartStr}>
            {/* Festival strip — one spanning block per festival (+ v2.25 : événements Google sur plusieurs jours) */}
            {(weekFests.length > 0 || weekBands.length > 0) && (
              <div className="grid grid-cols-7 px-0.5 pt-0.5 pb-px gap-x-0" style={{ gridAutoFlow: 'row dense' }}>  {/* v2.25 : bandes qui ne se chevauchent pas sur la même ligne */}
                {weekFests.map(f => {
                  const dispStart = format(festDisplayStart(f), 'yyyy-MM-dd');
                  const dispEnd = format(festDisplayEnd(f), 'yyyy-MM-dd');
                  const startIdx = Math.max(0, weekRow.findIndex(d => format(d, 'yyyy-MM-dd') >= dispStart));
                  const endIdx = Math.min(6, (() => {
                    for (let i = 6; i >= 0; i--) {
                      if (format(weekRow[i], 'yyyy-MM-dd') <= dispEnd) return i;
                    }
                    return 0;
                  })());
                  const isClipLeft = dispStart < weekStartStr;
                  const isClipRight = dispEnd > weekEndStr;
                  return (
                    <div key={f.id}
                      style={{ gridColumn: `${startIdx + 1} / ${endIdx + 2}`, backgroundColor: f.color || '#F59E0B' }}
                      className={cn(
                        'text-white text-[10.5px] leading-[14px] font-bold px-[3px] mt-px flex items-center gap-0.5 truncate',
                        isClipLeft ? 'rounded-r' : isClipRight ? 'rounded-l' : 'rounded',
                        f.optional ? 'opacity-40' : 'opacity-100'
                      )}
                      data-testid="festival-month-block">
                      <FestIcons festival={f} size={9} />
                      <span className="truncate">{f.name}</span>
                    </div>
                  );
                })}
                {weekBands.map(evt => {
                  const d = eventDays(evt);
                  const startIdx = Math.max(0, weekRow.findIndex(day => format(day, 'yyyy-MM-dd') >= d[0]));
                  let endIdx = 0;
                  for (let i = 6; i >= 0; i--) { if (format(weekRow[i], 'yyyy-MM-dd') <= d[d.length - 1]) { endIdx = i; break; } }
                  const clipL = d[0] < weekStartStr, clipR = d[d.length - 1] > weekEndStr;
                  return (
                    <a key={evt.id} href={evt.link || '#'} target="_blank" rel="noopener noreferrer"
                      style={{ gridColumn: `${startIdx + 1} / ${endIdx + 2}`, ...gcalStyle(evt) }}
                      className={cn('text-[10.5px] leading-[14px] font-bold px-[3px] mt-px flex items-center gap-0.5 truncate',
                        clipL && clipR ? '' : clipL ? 'rounded-r' : clipR ? 'rounded-l' : 'rounded')}
                      title={`${evt.calendar_name || 'Google'} · ${evt.title}`} data-testid="gcal-month-band">
                      <span className="truncate">{evt.title}</span>
                    </a>
                  );
                })}
              </div>
            )}

            {/* Day cells */}
            <div className="grid grid-cols-7 border-b border-slate-100">
              {weekRow.map(day => {
                const key = format(day, 'yyyy-MM-dd');
                const dayCls = classesByDay[key] || [];
                const inMonth = isSameMonth(day, displayMonth);
                const todayFlag = isToday(day);
                return (
                  <div key={key}
                    className={cn(
                      // v2.25 : cases un peu plus hautes, mais le mois entier tient sur l'iPhone (6 semaines → un peu moins hautes)
                      weekRows.length >= 6 ? 'min-h-[80px]' : 'min-h-[108px]',
                      'pt-0.5 border-r border-slate-100 cursor-pointer hover:bg-slate-50/80 transition-colors last:border-r-0 min-w-0',
                      !inMonth && 'bg-slate-50/40',
                      selectedDay && isSameDay(day, selectedDay) && 'bg-[#0288D1]/10 ring-2 ring-inset ring-[#0288D1]/40'
                    )}
                    onClick={() => onDayClick(day)}
                    data-testid={`month-day-${key}`}>
                    <div className={cn(
                      'mx-auto w-4 h-4 flex items-center justify-center rounded-full text-[11px] font-bold mb-px',
                      todayFlag ? 'bg-[#0288D1] text-white' : (inMonth ? 'text-slate-700' : 'text-slate-300')
                    )}>
                      {format(day, 'd')}
                    </div>
                    {(() => {
                      // v2.25 : jusqu'à 5 événements par jour (comme Google Agenda), plus petits mais lisibles
                                            // v2.25 : comme l'app Google Agenda — pastille pleine largeur, 16 px, texte 10,5 px ; l'heure (fine) puis le titre (gras)
                      // heure courte à la française : « 8h », « 19h30 » (plus de place pour le titre)
                      const hhmm = (iso) => { try { const d = parseISO(iso); return d.getMinutes() ? format(d, "H'h'mm") : format(d, "H'h'"); } catch { return ''; } };
                      const T = ({ iso }) => <span className="text-[9px] font-normal opacity-90 shrink-0">{hhmm(iso)}</span>;
                      const pill = 'w-full rounded-[3px] px-[3px] flex items-center gap-0.5 text-[10.5px] leading-[15px] font-bold truncate';
                      const items = [
                        // « toute la journée » d'abord, comme Google
                        ...(gcalByDay[key] || []).filter(e => !!e.is_all_day).map(evt => (
                          <a key={evt.id} href={evt.link || '#'} target="_blank" rel="noopener noreferrer" className={pill}
                            style={gcalStyle(evt)} title={`${evt.calendar_name || 'Google'} · ${evt.title}`}
                            onClick={e => e.stopPropagation()} data-testid="gcal-month-event">
                            {!evt.is_all_day && <T iso={evt.datetime_start} />}<span className="truncate">{evt.title}</span>
                          </a>
                        )),
                        ...dayCls.map(cls => (
                          <div key={cls.id} className={cn(pill, 'text-white')} title={cls.title}
                            style={{ backgroundColor: cls.color || (cls.type === 'group' ? '#0288D1' : '#26A69A') }}>
                            <T iso={cls.datetime_start} /><span className="truncate">{cls.title || (cls.type === 'group' ? 'Collectif' : 'Privé')}</span>
                          </div>
                        )),
                        ...(personalByDay[key] || []).map(evt => (
                          <div key={evt.id} className={cn(pill, 'text-white')} style={{ backgroundColor: evt.color || '#607D8B' }}
                            title={evt.title} data-testid="personal-month-event">
                            <T iso={evt.datetime_start} /><span className="truncate">{evt.title}</span>
                          </div>
                        )),
                        ...(gcalByDay[key] || []).filter(e => !e.is_all_day).map(evt => (
                          <a key={evt.id} href={evt.link || '#'} target="_blank" rel="noopener noreferrer" className={pill}
                            style={gcalStyle(evt)} title={`${evt.calendar_name || 'Google'} · ${evt.title}`}
                            onClick={e => e.stopPropagation()} data-testid="gcal-month-event">
                            {!evt.is_all_day && <T iso={evt.datetime_start} />}<span className="truncate">{evt.title}</span>
                          </a>
                        )),
                        ...(gtaskByDay[key] || []).map(evt => (
                          <div key={evt.id} className={pill} style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
                            title={evt.title} data-testid="gtask-month-event">
                            <span className="truncate">✓ {evt.title}</span>
                          </div>
                        )),
                      ];
                      return (
                        <div className="space-y-px px-px">
                          {/* 5 lignes max par jour (comme Google) : au-delà, la 5e ligne devient « +N » */}
                          {items.length <= 5 ? items : items.slice(0, 4)}
                          {items.length > 5 && <div className="text-[10.5px] leading-[15px] font-bold text-slate-500 text-center">+{items.length - 4}</div>}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PersonalEventCard({ evt, onClick }) {
  const time = (() => { try { return format(parseISO(evt.datetime_start), 'HH:mm'); } catch { return ''; } })();
  return (
    <button onClick={onClick}
      className="w-full rounded px-1.5 py-0.5 text-white leading-tight text-left hover:opacity-80 transition-opacity"
      style={{ backgroundColor: evt.color || '#607D8B' }}
      data-testid="personal-event-card">
      <div className="text-[10px] font-bold truncate">{time && `${time} `}{evt.title}</div>
    </button>
  );
}

function MobilePersonalCard({ evt, onClick }) {
  const time = (() => { try { return format(parseISO(evt.datetime_start), 'HH:mm'); } catch { return ''; } })();
  return (
    <button onClick={onClick}
      className="w-full rounded-2xl p-4 border flex items-start gap-3 text-left"
      style={{ backgroundColor: (evt.color || '#607D8B') + '15', borderColor: (evt.color || '#607D8B') + '40' }}
      data-testid="personal-mobile-card">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ backgroundColor: evt.color || '#607D8B' }}>
        <User size={16} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: evt.color || '#607D8B' }}>Personnel</div>
        <div className="text-lg font-extrabold text-slate-900 font-manrope">{time || '—'}</div>
        <div className="text-sm font-medium text-slate-700 truncate">{evt.title}</div>
        {evt.notes && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{evt.notes}</div>}
      </div>
    </button>
  );
}

function PersonalEventModal({ event, defaultDate, onClose, onSaved }) {
  const COLORS = ['#607D8B', '#F44336', '#FF9800', '#4CAF50', '#2196F3', '#9C27B0', '#E91E63', '#795548'];
  const toInputDT = (iso) => { try { return iso ? iso.slice(0, 16) : ''; } catch { return ''; } };
  const [form, setForm] = useState({
    title: event?.title || '',
    datetime_start: toInputDT(event?.datetime_start) || (defaultDate ? `${defaultDate}T18:00` : ''),
    datetime_end: toInputDT(event?.datetime_end) || (defaultDate ? `${defaultDate}T19:00` : ''),
    notes: event?.notes || '',
    color: event?.color || '#607D8B',
  });
  const [showNotes, setShowNotes] = useState(!!event?.notes);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim() || !form.datetime_start) {
      toast.error('Titre et date requis');
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      datetime_start: form.datetime_start.length === 16 ? form.datetime_start + ':00' : form.datetime_start,
      datetime_end: form.datetime_end ? (form.datetime_end.length === 16 ? form.datetime_end + ':00' : form.datetime_end) : null,
      notes: form.notes || null,
      color: form.color,
    };
    try {
      if (event?.id) { await api.put(`/personal-events/${event.id}`, payload); }
      else { await api.post('/personal-events', payload); }
      toast.success(event?.id ? 'Événement modifié' : 'Événement créé');
      onSaved();
    } catch { toast.error('Erreur enregistrement'); setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    await api.delete(`/personal-events/${event.id}`);
    toast.success('Supprimé');
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-3.5 h-3.5 rounded-full shrink-0" style={{ backgroundColor: form.color }} />
            <span className="font-extrabold text-slate-900 font-manrope">
              {event?.id ? 'Modifier' : 'Nouvel événement personnel'}
            </span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200">
            <X size={15} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Titre *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="input-base" placeholder="Ex: Kiné, Réunion, Administratif…"
              data-testid="personal-event-title" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Début *</label>
              <input type="datetime-local" value={form.datetime_start}
                onChange={e => setForm(f => ({ ...f, datetime_start: e.target.value }))}
                className="input-base" data-testid="personal-event-start" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Fin</label>
              <input type="datetime-local" value={form.datetime_end || ''}
                onChange={e => setForm(f => ({ ...f, datetime_end: e.target.value }))}
                className="input-base" data-testid="personal-event-end" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Couleur</label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map(c => (
                <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                  className={cn('w-8 h-8 rounded-full border-2 transition-transform', form.color === c ? 'border-slate-700 scale-110' : 'border-transparent')}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          {!showNotes ? (
            <button onClick={() => setShowNotes(true)}
              className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors">
              + Ajouter une note (optionnel)
            </button>
          ) : (
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Note</label>
              <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="input-base resize-none" rows={3} placeholder="Notes, rappels…" />
            </div>
          )}
          <div className="flex gap-2 pt-1">
            {event?.id && (
              <button onClick={remove}
                className="h-12 px-4 rounded-xl bg-red-50 text-red-500 font-bold text-sm hover:bg-red-100 transition-colors">
                Supprimer
              </button>
            )}
            <button onClick={onClose}
              className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm hover:bg-slate-200 transition-colors">
              Annuler
            </button>
            <button onClick={save} disabled={saving}
              className="flex-1 h-12 rounded-xl text-white font-bold text-sm disabled:opacity-60 transition-colors"
              style={{ backgroundColor: form.color }}
              data-testid="save-personal-event-btn">
              {saving ? '…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// v2.21 : plein = couleur de l'agenda, texte blanc ; transparent = teinte claire + trait de couleur
const gcalStyle = (evt) => {
  const c = evt.calendar_color || '#4285F4';
  return evt.solid ? { backgroundColor: c, color: '#fff', borderLeft: `3px solid ${c}` }
    : { backgroundColor: `${c}22`, color: '#1E293B', borderLeft: `3px solid ${c}` };
};

function GCalEventCard({ evt }) {
  const time = (() => { try { return evt.is_all_day ? '' : format(parseISO(evt.datetime_start), 'HH:mm'); } catch { return ''; } })();
  return (
    <a href={evt.link || '#'} target="_blank" rel="noopener noreferrer"
      className="flex items-center gap-1 w-full rounded px-1.5 py-0.5 leading-tight transition-opacity hover:opacity-80"
      style={gcalStyle(evt)}
      title={`${evt.calendar_name || 'Google'} · ${evt.title}`}
      data-testid="gcal-event-card">
      <span className="text-[9px] font-extrabold shrink-0">G</span>
      {time && <span className="text-[10px] font-bold shrink-0">{time}</span>}
      <span className="text-[10px] truncate">{evt.title}</span>
    </a>
  );
}

function MobileGCalCard({ evt }) {
  const time = (() => { try { return evt.is_all_day ? 'Toute la journée' : format(parseISO(evt.datetime_start), 'HH:mm'); } catch { return ''; } })();
  const c = evt.calendar_color || '#4285F4';
  const solid = !!evt.solid;  // v2.21 : plein ou transparent, choisi par agenda
  return (
    <a href={evt.link || '#'} target="_blank" rel="noopener noreferrer"
      className="rounded-2xl p-4 flex items-start gap-3"
      style={solid ? { backgroundColor: c } : { backgroundColor: `${c}1A`, borderLeft: `4px solid ${c}` }}
      data-testid="gcal-mobile-card">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-bold px-2 py-0.5 rounded-full"
            style={solid ? { backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff' } : { backgroundColor: c, color: '#fff' }}>{evt.calendar_name || 'Google'}</span>
        </div>
        <div className={cn('text-lg font-extrabold font-manrope', solid ? 'text-white' : 'text-slate-900')}>{time}</div>
        <div className={cn('text-sm font-medium truncate', solid ? 'text-white' : 'text-slate-700')}>{evt.title}</div>
        {evt.location_str && <div className={cn('text-xs mt-0.5 truncate', solid ? 'text-white/80' : 'text-slate-500')}>{evt.location_str}</div>}
      </div>
    </a>
  );
}

function GTaskEventCard({ evt }) {
  return (
    <div
      className="flex items-center gap-1 w-full rounded px-1.5 py-0.5 leading-tight"
      style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
      title={`${evt.title}${evt.notes ? ' — ' + evt.notes : ''}`}
      data-testid="gtask-event-card"
    >
      <span className="text-[9px] font-extrabold shrink-0">T</span>
      <span className="text-[10px] truncate">{evt.title}</span>
    </div>
  );
}

function MobileGTaskCard({ evt }) {
  return (
    <div className="bg-green-50 rounded-2xl p-4 border border-green-100 flex items-start gap-3" data-testid="gtask-mobile-card">
      <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
        <span className="text-green-700 font-extrabold text-sm">T</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-bold text-green-600 px-2 py-0.5 rounded-full bg-green-100">Google Tasks</span>
          {evt.tasklist_title && <span className="text-xs text-green-500">{evt.tasklist_title}</span>}
        </div>
        <div className="text-sm font-extrabold text-slate-900 font-manrope">{evt.title}</div>
        {evt.notes && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{evt.notes}</div>}
      </div>
    </div>
  );
}

function SmallClassCard({ cls, onClick, highlighted }) {
  const time = (() => { try { return format(parseISO(cls.datetime_start), 'HH:mm'); } catch { return ''; } })();
  const color = cls.color || (cls.type === 'group' ? '#0288D1' : '#26A69A');
  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-lg px-2 py-1.5 cursor-pointer hover:opacity-80 active:scale-95 transition-all text-white text-xs',
        highlighted && 'ring-2 ring-white ring-offset-2 ring-offset-slate-100 scale-[1.03] shadow-lg'
      )}
      style={{ backgroundColor: color }}
      data-testid="small-class-card"
    >
      <div className="font-bold">{time}</div>
      <div className="opacity-90 truncate">{cls.title || (cls.type === 'group' ? 'Collectif' : 'Privé')}</div>
      <div className="opacity-70">{fmtDuration(cls.duration_minutes)}</div>
    </div>
  );
}

function MobileClassCard({ cls, onClick }) {
  const time = (() => { try { return format(parseISO(cls.datetime_start), 'HH:mm'); } catch { return ''; } })();
  const color = cls.color || (cls.type === 'group' ? '#0288D1' : '#26A69A');
  const isGroup = cls.type === 'group';
  return (
    <div onClick={onClick} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm cursor-pointer active:scale-[0.98] transition-all" data-testid="mobile-class-card">
      <div className="flex items-start justify-between">
        <div>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: color }}>
            {isGroup ? 'Collectif' : 'Privé'}
          </span>
          <div className="text-xl font-extrabold text-slate-900 mt-1 font-manrope">{time}</div>
          {cls.title && <div className="text-sm font-medium text-slate-700">{cls.title}</div>}
        </div>
        <div className="text-right text-xs text-slate-500 space-y-1">
          <div className="flex items-center gap-1 justify-end"><Clock size={12} />{fmtDuration(cls.duration_minutes)}</div>
          {cls.location?.venue_name && <div className="flex items-center gap-1 justify-end"><MapPin size={12} />{cls.location.venue_name}</div>}
          <div className="flex items-center gap-1 justify-end"><Users size={12} />{cls.student_count || 0}</div>
        </div>
      </div>
    </div>
  );
}
